import {
  Button,
  Checkbox,
  CircularProgress,
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Progress,
  Sheet,
  SheetBody,
  SheetContent,
  SheetHeader,
  TextArea,
  TextField,
  toast,
  Tooltip,
  Typography,
} from "@suraasa/placebo-ui"
import api from "api"
import { AISuggestedRubric, ReviewRubric } from "api/resources/review/types"
import {
  AiDetectedSubmissionReport,
  PreviousSubmissionListItem,
  Submission,
} from "api/resources/submissions/types"
import { AISparkleFilled } from "assets/icons/AISparkle"
import GenerateComment from "assets/icons/GenerateComment.svg"
import clsx from "clsx"
import AsyncBuilder from "components/AsyncBuilder"
import { Copy, NavArrowDown, Plus } from "iconoir-react"
import capitalize from "lodash/capitalize"
import { useEffect, useState } from "react"
import { Controller, useFieldArray, useForm } from "react-hook-form"
import { formatDate, handleErrors, pluralize, SetState } from "utils/helpers"
import { isPIIRedactionEnabled, redactPII } from "utils/pii"

import StatusPill from "../../../components/ReviewStatusTag"
import { CurrentReview } from "."
import RejectSubmission from "./RejectSubmission"
import SubmitReview from "./SubmitReview"

type Props = {
  onClose: () => void
  open: boolean
  data: Submission | undefined
  setId: SetState<string>
  submissionData: PreviousSubmissionListItem[] | undefined
  submissionId: string
  rubrics: ReviewRubric[]
  refresh: () => Promise<any>
  onCopyFromPrevious: () => Promise<void>
  onCellClick: (rubricId: string, cellId: string) => void
  onCommentUpdate: (comment: string) => void
  currentReview: CurrentReview
  suggestedRubrics: AISuggestedRubric[] | undefined
  submissionLoading: boolean
  aiDetectedReportData: AiDetectedSubmissionReport | undefined
}
const AI_DETECTION_SLUG = "ai-generated-content"
type PenaltiesType = {
  reason: string
  percentage: string | number | null
  enabled: boolean
  isDefault: boolean
  slug?: string
  isDisabled?: boolean
  isChecked?: boolean
}
const defaultPenalties: PenaltiesType[] = [
  {
    reason: "Missing/Incorrect Content",
    percentage: null,
    enabled: false,
    isDefault: true,
  },
  {
    reason: "Empty Submission",
    percentage: null,
    enabled: false,
    isDefault: true,
  },
  {
    reason: "Irrelevant Content",
    percentage: null,
    enabled: false,
    isDefault: true,
  },
  {
    reason: "Plagiarised Content",
    percentage: null,
    enabled: false,
    isDefault: true,
  },
  {
    reason: "AI-Generated Content",
    percentage: null,
    enabled: false,
    isDefault: true,
    slug: AI_DETECTION_SLUG,
  },
]

const getMarkDeductionByAIPercentage = (percentage: number): number => {
  if (percentage < 50) return 0
  // 50–79%, including decimals such as 79.5
  if (percentage < 80) return 20
  if (percentage <= 90) return 50
  if (percentage > 90) return 100
  return 0
}

const getTooltipEnable = ({
  isDisabled,
  value,
}: {
  isDisabled?: boolean
  value: any | null
}) => {
  if (isDisabled) {
    return false
  }
  if (value) {
    return false
  }
  return true
}

const RubricSheet = ({
  onClose,
  open,
  data,
  setId,
  submissionData,
  submissionId,
  rubrics,
  onCopyFromPrevious,
  onCellClick,
  currentReview,
  onCommentUpdate,
  refresh,
  suggestedRubrics,
  submissionLoading,
  aiDetectedReportData,
}: Props) => {
  const { review } = data || { review: undefined }
  const isRedacted = isPIIRedactionEnabled()
  const [showReject, setShowReject] = useState(false)
  const [showAccept, setShowAccept] = useState(false)
  const [generatingComments, setGeneratingComments] = useState(false)
  const [disableSelectRubrics, setDisableSelectRubrics] = useState(false)

  const isAlreadyReviewed = Boolean(
    review?.comments || review?.marks || review?.result
  )

  useEffect(() => {
    if (isAlreadyReviewed) {
      reset({
        penalties: review?.penalties
          ? review.penalties.map(p => ({
              reason: p.reason,
              percentage: p.percentage,
              enabled: true,
              isDefault: false,
            }))
          : defaultPenalties,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAlreadyReviewed])

  useEffect(() => {
    // This is to support the copy from previous review feature
    // Using custom events so we don't have to pass state around from this component to the previous component.
    const listener = (e: Event) => {
      const customEvent = e as CustomEvent
      reset({
        penalties: customEvent.detail.map((d: any) => ({
          reason: d.reason,
          percentage: d.percentage,
          enabled: true,
          isDefault: false,
        })),
      })
    }
    document.addEventListener("reset-penalties", listener as EventListener)

    return () => {
      document.removeEventListener("reset-penalties", listener)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const { getValues, setValue, register, control, reset } = useForm<{
    penalties: PenaltiesType[]
  }>({
    defaultValues: {
      penalties: review?.penalties
        ? review.penalties.map(p => ({
            reason: p.reason,
            percentage: p.percentage,
            enabled: true,
            isDefault: false,
          }))
        : defaultPenalties,
    },
  })
  const { fields, append } = useFieldArray({
    control,
    name: "penalties",
  })

  const onGenerateComments = async () => {
    setDisableSelectRubrics(true)
    setGeneratingComments(true)
    const rubricsData = rubrics.map(r => {
      const selectedCommentId = currentReview.selected.find(
        cr => cr.rubricId === r.id
      )
      const selectedComment = r.allComments.find(
        r => r.uuid === selectedCommentId?.commentId
      )

      return {
        criterion: r.criteria,
        score: Number(selectedComment?.scale),
        rubric_cells: r.allComments.map(r => ({
          comment: r.comments,
          scale: r.scale,
        })),
      }
    })

    try {
      const res = await api.review.generateAIFeedback({
        urlParams: {
          submissionId,
        },
        data: {
          rubrics: rubricsData,
        },
      })
      if (res.feedback) {
        onCommentUpdate(res.feedback)
      }
    } catch (error) {
      handleErrors(error)
    }
    setGeneratingComments(false)
    setDisableSelectRubrics(false)
  }
  useEffect(() => {
    if (
      !aiDetectedReportData ||
      aiDetectedReportData.aiCheckStatus !== "completed" ||
      isAlreadyReviewed
    )
      return

    const deductionPercentage = getMarkDeductionByAIPercentage(
      Number(aiDetectedReportData.aiWrittenPercentage)
    )

    // Skip if no deduction
    if (deductionPercentage === 0) return

    const previousPenalties = getValues("penalties").map(item => {
      if (item.slug === AI_DETECTION_SLUG) {
        return {
          ...item,
          percentage: deductionPercentage.toString(),
          isDisabled: true,
          enabled: true,
        }
      }
      return item
    })

    setValue("penalties", previousPenalties)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiDetectedReportData])

  const previousSubmissionId = submissionData && submissionData[1]?.uuid
  const isUnderReview = review?.status === "under_review"
  const isNotReviewed = data && data.status === "not_reviewed"

  const canAccept =
    isUnderReview &&
    currentReview.comment &&
    currentReview.selected.length === rubrics.length

  const canReject = isUnderReview && currentReview.comment

  const onAcceptSubmission = async () => {
    if (!submissionData) {
      setShowAccept(false)
      return
    }

    if (currentReview.comment.length < 4) {
      toast.error("Comments should have minimum 4 characters")
      return
    }

    setDisableSelectRubrics(true)
    try {
      const data = {
        professor_comments: currentReview.comment,
        review: submissionData[0].review?.uuid,
        rubric_cells: currentReview.selected.map(r => r.commentId),

        // Don't use `fields` here directly, it's gives stale state.
        penalties: getValues("penalties")
          .filter(x => x.enabled && x.reason && x.percentage)
          .map(field => ({
            reason: field.reason,
            percentage: field.percentage,
          })),
      }

      await api.review.createReviewRubrics({
        data,
      })
      await refresh()
    } catch (error) {
      handleErrors(error)
    }
    setDisableSelectRubrics(false)
    setShowAccept(false)
  }

  const onStartReview = async () => {
    if (!data) {
      return
    }
    try {
      const payload = {
        assignment_id: data.assignmentId,
        submission_id: data.uuid,
        user_id: data.userId,
      }

      await api.review.create({
        data: payload,
      })
      await refresh()
    } catch (error) {
      handleErrors(error)
    }
  }

  const onReject = async (isPlagiarised: boolean) => {
    if (!review?.uuid) {
      return
    }

    if (currentReview.comment.length < 4) {
      toast.error("Comments should have minimum 4 characters")
      return
    }

    setDisableSelectRubrics(true)
    try {
      await api.review.update({
        urlParams: {
          reviewId: review.uuid,
        },
        data: {
          comments: currentReview.comment,
          status: isPlagiarised ? "plagiarised" : "rejected",
        },
      })

      await refresh()
      setShowReject(false)
    } catch (error) {
      handleErrors(error)
    }
    setDisableSelectRubrics(false)
  }

  const isAiSuggested = (commentId: string) => {
    return (
      isUnderReview && !!suggestedRubrics?.find(r => r.rubricCell === commentId)
    )
  }

  return (
    <>
      <RejectSubmission
        open={showReject}
        onClose={() => setShowReject(false)}
        onReject={onReject}
      />
      <SubmitReview
        open={showAccept}
        onClose={() => setShowAccept(false)}
        onAccept={onAcceptSubmission}
      />
      <Sheet
        open={open}
        onOpenChange={open => {
          if (!open) {
            onClose()
          }
        }}
      >
        <SheetContent
          height={90}
          side="right"
          className="w-screen md:w-3/4 md:!max-w-none"
          closeWhenInteractOutside
        >
          <SheetHeader>
            <div className="mr-2 flex w-full flex-wrap items-center justify-between">
              <div>
                <div className="mb-0.5 flex gap-1">
                  {submissionData && (
                    <div>
                      <DropdownMenu>
                        <DropdownMenuTrigger>
                          <Typography
                            variant="strong"
                            className="flex items-center gap-0.5"
                          >
                            Previous Submissions <NavArrowDown />
                          </Typography>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent>
                          <DropdownMenuGroup>
                            {submissionData
                              .sort((a, b) => b.order - a.order)
                              .map((s, i) => (
                                <DropdownMenuItem
                                  key={i}
                                  onClick={() => setId(s.uuid)}
                                  className={cn(
                                    submissionId === s.uuid &&
                                      "bg-primary-500 text-surface focus:!bg-primary-500 focus:!text-surface"
                                  )}
                                >
                                  <div>
                                    <Typography>
                                      Submission {submissionData.length - i}{" "}
                                      {submissionData.length - i ===
                                        submissionData.length && "(Latest)"}
                                    </Typography>
                                    <Typography variant="smallBody">
                                      Score:{" "}
                                      {s.marks != null
                                        ? s.marks.toFixed(2)
                                        : "Not yet scored"}
                                    </Typography>
                                  </div>
                                </DropdownMenuItem>
                              ))}
                          </DropdownMenuGroup>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-1">
                  <div className="flex flex-col md:flex-row md:items-center md:gap-1">
                    <Typography>
                      Score:{" "}
                      {review?.marks != null
                        ? Number(review.marks).toFixed(2)
                        : "Not yet scored"}
                    </Typography>
                    <div className="hidden h-full w-0.25 bg-onSurface-500 md:block">
                      &nbsp;
                    </div>
                    <div className="flex items-center gap-0.5">
                      <Typography>Status:</Typography>
                      <StatusPill
                        result={review?.result}
                        status={review?.status}
                        order={undefined}
                      />
                    </div>
                  </div>
                  <div className="flex flex-col gap-1 md:flex-row md:items-center">
                    <Typography>
                      Reviewer:{" "}
                      {review?.professor
                        ? isRedacted
                          ? redactPII(review.professor)
                          : review.professor
                        : "Not available"}
                    </Typography>
                    {review?.datePublished && (
                      <>
                        <div className="hidden h-full w-0.25 bg-onSurface-500 md:block">
                          &nbsp;
                        </div>
                        <Typography>
                          {formatDate(review.datePublished, "MMM dd, yyyy")}
                        </Typography>
                      </>
                    )}
                  </div>
                </div>
              </div>
              <div>
                {isNotReviewed && (
                  <AsyncBuilder
                    handler={onStartReview}
                    render={({ loading, onClick }) => (
                      <Button
                        loading={loading}
                        onClick={onClick}
                        size="sm"
                        disabled={!data}
                      >
                        Start Review
                      </Button>
                    )}
                  />
                )}
              </div>
            </div>
          </SheetHeader>
          {submissionLoading ? (
            <div className="absolute inset-0 flex items-center justify-center">
              <CircularProgress />
            </div>
          ) : (
            <SheetBody className="flex flex-col gap-3">
              <div>
                <Typography variant="title3" className="mb-1">
                  Rubrics
                </Typography>
                <div className="flex flex-col gap-3">
                  {rubrics.map(
                    ({ task, criteria, allComments, marked, id }, i) => (
                      <div
                        key={i}
                        className={cn(
                          "flex flex-col gap-1",
                          isUnderReview && "mb-0.5"
                        )}
                      >
                        <Typography variant="strong">
                          {[task, capitalize(criteria)]
                            .filter(Boolean)
                            .join(") ")}
                        </Typography>
                        <div className="grid gap-2 md:grid-cols-3">
                          {allComments
                            .sort((a, b) => a.scale - b.scale)
                            .map(({ comments, scale, uuid }, index) => {
                              const isMarked =
                                marked?.uuid === uuid ||
                                !!currentReview.selected.find(
                                  s => s.commentId === uuid
                                )

                              return (
                                <div key={index} className={cn("relative")}>
                                  <button
                                    onClick={() => {
                                      if (isUnderReview) {
                                        onCellClick(id, uuid)
                                      }
                                    }}
                                    disabled={
                                      !isUnderReview || disableSelectRubrics
                                    }
                                    className={cn(
                                      isAiSuggested(uuid) &&
                                        "border border-[#39affd] ",
                                      "group flex text-start rounded-lg flex-grow h-full w-full"
                                    )}
                                  >
                                    <div
                                      className={cn(
                                        "h-full flex flex-col grow gap-1 rounded-lg border p-2",
                                        {
                                          "bg-primary-500 text-surface":
                                            isMarked,
                                        }
                                      )}
                                    >
                                      <Typography variant="title4">
                                        {pluralize("Point", scale)}
                                      </Typography>
                                      <Typography
                                        variant="smallBody"
                                        className={cn({
                                          "text-onSurface-800": !isMarked,
                                          "text-surface": isMarked,
                                        })}
                                      >
                                        {comments}
                                      </Typography>
                                    </div>
                                  </button>
                                  {isAiSuggested(uuid) && (
                                    <div className="absolute -bottom-1.25 flex w-full items-center justify-center">
                                      <div className="-mt-0.25 flex items-center justify-center rounded-xl bg-[#39affd] px-2 text-xs text-surface">
                                        <AISparkleFilled
                                          className="mr-0.5 h-full p-0.25"
                                          viewBox="0 0 20 20"
                                        />
                                        AI Suggested
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )
                            })}
                        </div>
                      </div>
                    )
                  )}
                </div>
              </div>

              {review && (
                <div>
                  <Typography variant="title3" className="mb-1">
                    Deductions
                  </Typography>

                  <div className="grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-3">
                    {fields.map((field, index) => {
                      return (
                        <div className="flex items-center gap-2" key={field.id}>
                          <div className="flex items-center gap-1">
                            <Controller
                              control={control}
                              name={`penalties.${index}.enabled`}
                              render={({ field: controllerField }) => (
                                <Checkbox
                                  checked={controllerField.value}
                                  onCheckedChange={controllerField.onChange}
                                />
                              )}
                            />

                            {field.isDefault ? (
                              <input
                                disabled={field.isDisabled}
                                placeholder="Deduction"
                                className="p-0.25 text-sm disabled:bg-transparent"
                                {...register(`penalties.${index}.reason`)}
                              />
                            ) : (
                              <input
                                disabled={field.isDisabled ?? isAlreadyReviewed}
                                placeholder="Deduction"
                                className="border-b border-onSurface-500 p-0.25 text-sm"
                                {...register(`penalties.${index}.reason`)}
                              />
                            )}
                          </div>

                          <Controller
                            control={control}
                            name={`penalties.${index}.enabled`}
                            render={({ field: controllerField }) => (
                              <Tooltip
                                enabled={getTooltipEnable({
                                  isDisabled: field.isDisabled,
                                  value: controllerField.value,
                                })}
                                content="Check the box to enable this deduction"
                              >
                                <TextField
                                  disabled={
                                    field.isDisabled ||
                                    isAlreadyReviewed ||
                                    !controllerField.value
                                  }
                                  dense
                                  type="number"
                                  placeholder="1-100"
                                  endIcon="%"
                                  className="max-w-16"
                                  {...register(
                                    `penalties.${index}.percentage`,
                                    {
                                      min: 1,
                                      max: 100,
                                    }
                                  )}
                                />
                              </Tooltip>
                            )}
                          />
                        </div>
                      )
                    })}

                    {!isAlreadyReviewed && (
                      <div>
                        <Button
                          endAdornment={<Plus className="!size-3" />}
                          variant="text"
                          onClick={() => {
                            append({
                              reason: "New Deduction",
                              percentage: null,
                              isDefault: false,
                              enabled: true,
                            })
                          }}
                        >
                          Add Another Deduction
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {review ? (
                <div className="flex flex-col gap-1">
                  <Typography variant="title3">Comments</Typography>
                  <div className="relative">
                    {generatingComments && (
                      <div className="absolute z-20 size-full rounded-md border border-onSurface-800 bg-surface">
                        <div className="flex h-full flex-col items-center justify-center gap-1">
                          <div className="flex flex-col gap-0.5 text-center">
                            <Typography variant="title3">
                              Generating Comments
                            </Typography>
                            <Typography
                              variant="body"
                              className="text-onSurface-500"
                            >
                              This might take a minute
                            </Typography>
                          </div>
                          <div className="mt-1 w-[220px] md:w-full md:max-w-sm">
                            <Progress indeterminate />
                          </div>
                        </div>
                      </div>
                    )}
                    {isUnderReview && (
                      <div className="absolute bottom-1 right-1 z-10">
                        <Tooltip
                          content="Ensure all rubrics are marked before proceeding"
                          enabled={
                            currentReview.selected.length !== rubrics.length
                          }
                        >
                          <button
                            className={clsx(
                              "group flex size-6 cursor-pointer items-center justify-between gap-1 overflow-hidden rounded-2xl bg-onSurface-900 p-1.5 shadow-md",
                              {
                                "transition-all duration-500 ease-in-out hover:aspect-auto hover:w-[190px] hover:bg-onSurface-900":
                                  currentReview.selected.length ===
                                  rubrics.length,
                                "opacity-50":
                                  currentReview.selected.length !==
                                  rubrics.length,
                              }
                            )}
                            onClick={onGenerateComments}
                            disabled={
                              currentReview.selected.length !== rubrics.length
                            }
                            style={{
                              cursor:
                                currentReview.selected.length !== rubrics.length
                                  ? "not-allowed"
                                  : "pointer",
                            }}
                          >
                            <div
                              className={clsx("duration-75 ease-in-out", {
                                "hidden group-hover:relative group-hover:block group-hover:w-auto group-hover:scale-100 group-hover:opacity-100":
                                  currentReview.selected.length ===
                                  rubrics.length,

                                hidden:
                                  currentReview.selected.length !==
                                  rubrics.length,
                              })}
                            >
                              <Typography
                                className={clsx(
                                  "absolute -z-10 whitespace-nowrap text-onSurface-50 opacity-0 transition-opacity duration-1000 ease-linear",
                                  {
                                    "group-hover:relative group-hover:block group-hover:opacity-100":
                                      currentReview.selected.length ===
                                      rubrics.length,
                                  }
                                )}
                                variant="smallBody"
                              >
                                Generate Comments
                              </Typography>
                            </div>

                            <img
                              src={GenerateComment}
                              alt="GenerateComment"
                              className="size-3.5"
                            />
                          </button>
                        </Tooltip>
                      </div>
                    )}

                    <TextArea
                      value={
                        isUnderReview ? currentReview.comment : review?.comments
                      }
                      minLength={4}
                      key={review?.uuid}
                      onChange={e => onCommentUpdate(e.target.value)}
                      rows={8}
                    />
                  </div>
                  <div className="flex flex-wrap items-center justify-between gap-0.5 md:flex-nowrap md:gap-0">
                    <div>
                      <AsyncBuilder
                        handler={onCopyFromPrevious}
                        render={({ loading, onClick }) => (
                          <Button
                            onClick={onClick}
                            variant="text"
                            startAdornment={<Copy className="size-2.5" />}
                            disabled={!previousSubmissionId || !isUnderReview}
                            loading={loading}
                          >
                            Copy previous review
                          </Button>
                        )}
                      />
                    </div>
                    <div className="flex w-full items-center justify-between gap-1 md:w-auto">
                      <Tooltip
                        content="Please add comments"
                        enabled={!canReject}
                      >
                        <Button
                          variant="text"
                          color="critical"
                          disabled={!canReject}
                          onClick={() => setShowReject(true)}
                        >
                          Reject Submission
                        </Button>
                      </Tooltip>

                      <Tooltip
                        content="Please mark all rubrics and add comments"
                        enabled={!canAccept}
                      >
                        <Button
                          size="sm"
                          disabled={!canAccept}
                          onClick={() => setShowAccept(true)}
                        >
                          Submit Review
                        </Button>
                      </Tooltip>
                    </div>
                  </div>
                </div>
              ) : (
                <Typography variant="title3" className="mb-1">
                  Press the Start Review button to start reviewing this
                  submission.
                </Typography>
              )}
            </SheetBody>
          )}
        </SheetContent>
      </Sheet>
    </>
  )
}

export default RubricSheet
